import { request } from '@/utils/request.js';
import { clearStorage } from '@/utils/storage.js';

/**
 * 侧边栏/历史记录功能 Mixin
 * 包含：侧边栏开关、历史会话列表、会话详情、结束对话、退出登录
 */
export default {
    data() {
        return {
            sidebarShow: false, // 侧边栏是否展开
            sidebarLoading: false, // 侧边栏是否加载中
            sidebarConversations: [], // 侧边栏会话列表
            sidebarShowDetail: false, // 是否显示历史详情弹窗
            sidebarDetailMessages: [], // 历史详情消息列表
            sidebarShowConfirm: false, // 是否显示通用确认弹窗（新会话/生成报告共用）
            sidebarConfirmType: '', // 确认弹窗类型：new-新会话 report-生成报告
        };
    },

    methods: {
        // 打开侧边栏并加载会话列表
        sidebarToggle() {
            this.sidebarShow = true;
            this.sidebarLoadConversations();
        },

        // 关闭侧边栏
        sidebarClose() {
            this.sidebarShow = false;
        },

        // 加载侧边栏会话列表（过滤掉当前进行中的会话）
        async sidebarLoadConversations() {
            this.sidebarLoading = true;
            try {
                const res = await request({
                    url: '/api/conversations',
                    method: 'GET',
                });
                if (res.data.code === 200) {
                    const list = res.data.data.conversations || [];
                    this.sidebarConversations = this.chatConversationId
                        ? list.filter(
                              (c) =>
                                  c.conversationId !== this.chatConversationId
                          )
                        : list;
                }
            } catch (e) {
                console.error('加载会话列表失败:', e);
            } finally {
                this.sidebarLoading = false;
            }
        },

        // 查看某个会话的详情
        async sidebarViewConversation(item) {
            try {
                const res = await request({
                    url: `/api/conversations/${item.conversationId}/messages`,
                    method: 'GET',
                });
                if (res.data.code === 200) {
                    this.sidebarDetailMessages = res.data.data.messages || [];
                    this.sidebarShowDetail = true;
                }
            } catch (e) {
                console.error('加载会话详情失败:', e);
                this.$toast('加载失败');
            }
        },

        // 跳转到报告页面
        sidebarViewReport(item) {
            this.sidebarClose();
            this.$router.push(`/report?conversationId=${item.conversationId}`);
        },

        // 格式化日期为 MM-DD HH:mm
        sidebarFormatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hour = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            return `${month}-${day} ${hour}:${min}`;
        },

        // 结束当前会话：停止播放/打字机，请求后端标记结束并清空本地会话，返回原会话ID
        async sidebarEndConversation() {
            this.audioStop();
            this.chatStopTypewriter();
            if (this.chatThinkingTimer) {
                clearTimeout(this.chatThinkingTimer);
                clearInterval(this.chatThinkingTimer);
                this.chatThinkingTimer = null;
            }
            const cid = this.chatConversationId;
            if (cid) {
                try {
                    await request({
                        url: '/api/end-conversation',
                        method: 'POST',
                        data: { conversationId: cid },
                    });
                } catch (e) {
                    console.error('标记会话结束失败:', e);
                }
            }
            this.chatConversationId = '';
            this.messages = [];
            return cid;
        },

        // 点击「新会话」：打开确认弹窗
        sidebarOpenNewSession() {
            this.sidebarConfirmType = 'new';
            this.sidebarShowConfirm = true;
        },

        // 点击「生成报告」：校验对话轮数（一轮=用户发言一次），满足后打开确认弹窗
        sidebarOpenReport() {
            // 仅统计用户消息，机器人回复与thinking占位不计入轮数
            const roundCount = this.messages.filter(
                (m) => m.type === 'user'
            ).length;
            if (roundCount < 10) {
                this.$toast('需聊满10轮才能生成总结报告~');
                return;
            }
            this.sidebarConfirmType = 'report';
            this.sidebarShowConfirm = true;
        },

        // 确认弹窗点击「确认」：按类型分发（新会话 / 生成报告）
        async sidebarConfirmAction() {
            const type = this.sidebarConfirmType;
            this.sidebarShowConfirm = false;
            const cid = await this.sidebarEndConversation();
            if (type === 'report') {
                this.$router.push(`/report?conversationId=${cid}`);
            } else {
                this.chatShowDefaultWelcome();
                this.$toast('已开启新会话');
            }
        },

        // 确认后结束当前会话并返回模式选择页（切换模式用，对齐参考项目回 choice 页交互）
        async sidebarLeaveToChoice(title, message, confirmText) {
            try {
                await this.$dialog.confirm({
                    title,
                    message,
                    confirmButtonText: confirmText,
                });
            } catch (e) {
                return; // 用户取消
            }
            this.sidebarClose();
            // 结束当前会话并清空本地状态，避免残留会话影响下一次模式选择
            await this.sidebarEndConversation();
            this.isAssessmentMode = false;
            this.isAssessmentWaiting = false;
            this.$router.replace('/choice');
        },

        // 切换模式：结束当前对话并回模式选择页重新选择（自由聊天/心理评估）
        sidebarSwitchMode() {
            this.sidebarLeaveToChoice(
                '切换模式',
                '切换模式将结束当前对话，当前对话内容不会保留，确定要切换吗？',
                '切换'
            );
        },

        // 评估模式新会话：确认后结束当前评估并直接重开新一轮评估（留在聊天页，不回模式选择页）
        async sidebarQuitAssessment() {
            try {
                await this.$dialog.confirm({
                    title: '开启新会话',
                    message:
                        '开启新会话将结束本次评估，本次评估内容不会保留，确定开启吗？',
                    confirmButtonText: '开启新会话',
                });
            } catch (e) {
                return; // 用户取消
            }
            this.sidebarClose();
            // 结束当前评估会话并清空消息，与普通模式新会话同一概念
            await this.sidebarEndConversation();
            this.isAssessmentWaiting = false;
            // 直接启动新一轮评估：推入「进行心理评估」触发第一问
            this.assessmentStartMode();
        },

        // 退出登录：弹窗确认（说明会话影响）后清除登录状态并跳转门户页
        async sidebarHandleLogout() {
            try {
                await this.$dialog.confirm({
                    title: '退出登录',
                    message: '退出后当前对话将不会保留，确定要退出吗？',
                });
            } catch (e) {
                return; // 用户取消
            }
            if (this.chatConversationId) {
                try {
                    await request({
                        url: '/api/end-conversation',
                        method: 'POST',
                        data: { conversationId: this.chatConversationId },
                    });
                } catch (e) {
                    console.error('标记会话结束失败:', e);
                }
            }
            if (this.audioRecordTimer) {
                clearInterval(this.audioRecordTimer);
            }
            if (this.chatTypingTimer) {
                clearInterval(this.chatTypingTimer);
            }
            if (this.chatThinkingTimer) {
                clearTimeout(this.chatThinkingTimer);
                clearInterval(this.chatThinkingTimer);
            }
            this.audioStop();
            clearStorage();
            this.$toast({ message: '已退出登录', duration: 1500 });
            setTimeout(() => {
                this.$router.replace('/portal');
            }, 1500);
        },
    },
};
